import { keys } from '@refinedev/core'
import type { SessionInfo } from '@sirius/contracts/admin'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router'
import type { AdminClient } from '~/api/adminClient'
import { refreshMe } from '~/providers/sessionQueries'
import { hostMembershipChange, hostRole } from './tenantDecision'

/**
 * Asks `auth.me` again on every navigation and when the window gets focus,
 * while a tenant user is in the host tenant `tenantId` (null: staff, or
 * nothing rendered). Removed from it, the page reloads, so the gate decides
 * again (their other tenant, or "No tenant assigned"); with another role in
 * it, Refine's access answers are asked again (menu, pages, buttons). The
 * gate's own query stays as it is: refetching it would unmount the app.
 *
 * The role is compared with the hook's own baseline (`loadedRole`, the role
 * the gate loaded, then the one each recheck saw), not with the cached
 * `auth.me`: the loader, the data provider and `can()` refresh that one too,
 * so a demotion they fetched first would look unchanged.
 */
export function useHostMembershipRecheck(
  client: AdminClient,
  tenantId: string | null,
  loadedRole: string | undefined,
) {
  const queryClient = useQueryClient()
  const { pathname } = useLocation()
  // the gate has just loaded `auth.me` when it renders: no check on mount
  const checkedPathRef = useRef(pathname)
  const reloadingRef = useRef(false)
  const baselineRef = useRef<{ tenantId: string; role: string | undefined }>(
    null,
  )
  if (tenantId !== null && baselineRef.current?.tenantId !== tenantId) {
    baselineRef.current = { tenantId, role: loadedRole }
  }

  useEffect(() => {
    if (tenantId === null) {
      return
    }
    const recheck = async () => {
      let after: SessionInfo
      try {
        after = await refreshMe(client, queryClient)
      } catch {
        // offline or signed out: the next API call reports it
        return
      }
      const baseline = baselineRef.current
      if (baseline?.tenantId !== tenantId) {
        return
      }
      const change = hostMembershipChange({
        roleBefore: baseline.role,
        after,
        tenantId,
      })
      baseline.role = hostRole(after, tenantId)
      if (change === 'lost' && !reloadingRef.current) {
        reloadingRef.current = true
        window.location.reload()
      } else if (change === 'roleChanged') {
        await queryClient.invalidateQueries({
          queryKey: keys().access().get(),
        })
      }
    }

    if (checkedPathRef.current !== pathname) {
      checkedPathRef.current = pathname
      void recheck()
    }
    const onFocus = () => {
      void recheck()
    }
    window.addEventListener('focus', onFocus)
    return () => {
      window.removeEventListener('focus', onFocus)
    }
  }, [client, queryClient, tenantId, pathname])
}
